/**
 * PasteFigurePlugin — 편집기에 **이미지가 들어오는 순간**을 가로채 호스트에 묻는다.
 *
 * ── 왜 그 순간인가 ──
 * 붙여넣기는 화면이 사람의 의도를 물을 수 있는 **유일한 시점**이다. 그 뒤에는 본문에 놓인
 * `image` 노드 하나가 「그림을 바꾸려던 것」인지 「참고로 끼워 둔 것」인지 가릴 방법이 없다.
 *
 * OCR 검수에서는 그 구분이 곧 사고로 이어진다. 서버가 세는 그림은 **원문(payload)** 의 것이고
 * (계약 §10 · BE-20260904 회신 §5), 편집본에만 생긴 노드는 목록에 들어오지 않는다. 그래서
 * 아무 말 없이 붙여넣게 두면 그 field 의 그림 편집이 통째로 잠기고, 그 상태에서 그림을 고치면
 * 편집본이 폐기된다. 물어보고 서버에 등록하거나 교체하면 그 두 가지가 애초에 일어나지 않는다.
 *
 * ── 왜 Provider 가 있을 때만 도는가 ──
 * `src/editor` 는 공용이고 소비자가 13곳인데 그중에 v1 유물 편집기 6곳과 인쇄 미리보기가 섞여
 * 있다. 거기서 붙여넣기를 가로채면 이 앱의 v2 계약과 무관한 화면이 멈춘다. 그래서
 * **`FigureEditContext` 에 `onPasteImage` 를 준 화면에서만** 등록한다 — 그림 편집 버튼이
 * 쓰는 것과 같은 수법이다(FigureEditContext 머리주석).
 *
 * ── 덮지 못하는 경로가 하나 있다 ──
 * 웹페이지에서 복사한 `<img>` 는 `importDOM` 으로 들어와(ImageNode.tsx) 이 명령을 타지 않는다.
 * 그 그물은 호스트가 저장 직전에 친다(그림 수 대조) — 여기서 잡는 것은 툴바 삽입 · 파일 드롭 ·
 * 클립보드 파일 붙여넣기 셋이고, 실제 검수에서 쓰이는 것도 그 셋이다.
 */
import { useEffect, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection, $isRangeSelection, COMMAND_PRIORITY_HIGH, COMMAND_PRIORITY_CRITICAL, DROP_COMMAND,
} from 'lexical';
import { $dfs } from '@lexical/utils';

import { $isImageNode } from '../nodes/ImageNode';
import { INSERT_IMAGE_COMMAND, InsertImagePayload } from './ImagesPlugin';
import { useFigureEdit } from '../context/FigureEditContext';

/**
 * 삽입 지점 **앞에** 있는 image 노드의 `src` 를 문서 순서로 모은다.
 *
 * 호스트가 이것으로 서버 순번(`at`)을 센다 — 커서 앞의 그림 중 **서버가 아는 것**만 세야 하는데,
 * 그 판정에 필요한 목록을 쥔 쪽이 호스트다. 여기서는 재료만 만들고 판정하지 않는다.
 *
 * 선택이 없거나 앵커를 순회에서 만나지 못하면 `null` 이다 — 「0장」과 「모른다」를 가른다.
 * 호스트는 null 을 받으면 `at` 을 싣지 않고, 서버는 그때 맨 끝에 넣는다(회신 §1).
 */
function $collectPrecedingImageSrcs(): (string | null)[] | null {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return null;
  const anchorKey = selection.anchor.getNode().getKey();
  const out: (string | null)[] = [];
  let met = false;
  // 세는 규칙은 계약 §8 과 같다 — `$dfs` 가 닿는 `type:'image'` 전부(캡션은 별도 editorState 라 안 닿는다).
  // eslint-disable-next-line no-restricted-syntax
  for (const { node } of $dfs()) {
    if (node.getKey() === anchorKey) { met = true; break; }
    if ($isImageNode(node)) out.push(node.getSrc());
  }
  return met ? out : null;
}

export default function PasteFigurePlugin(): null {
  const [editor] = useLexicalComposerContext();
  const figureEdit = useFigureEdit();
  const onPasteImage = figureEdit?.onPasteImage;
  const field = figureEdit?.field;
  /**
   * **편집기 안에서 그림을 옮기는 중인가**(2026-09-04 · 오너 보고 「위치 바꿀 때도 모달이 떴다가
   * 넣었던 그림이 제거됨」).
   *
   * Lexical 의 이미지 드래그 이동은 **지우고 다시 넣기**다 — `ImagesPlugin.onDrop` 이
   * `node.remove()` 뒤에 `INSERT_IMAGE_COMMAND` 를 다시 쏜다(`ImagesPlugin.tsx:332·338`).
   * 그 재삽입까지 가로채면 창이 한 번 더 뜨고, **삽입을 막으므로 이미 지워진 그림이 돌아오지
   * 못한다.** 사람이 자리만 옮기려 했는데 그림이 사라진다.
   *
   * 가르는 값은 드래그 데이터다 — 편집기 안에서 시작한 드래그에만
   * `application/x-lexical-drag` 가 실린다(`ImagesPlugin.tsx:290·354`). 밖에서 끌어온 파일에는
   * 없으므로 그쪽은 종전대로 새 이미지로 다룬다.
   */
  const movingRef = useRef(false);

  useEffect(() => {
    if (typeof onPasteImage !== 'function') return undefined;
    const unregisterDrop = editor.registerCommand<DragEvent>(
      DROP_COMMAND,
      (event) => {
        const types = event?.dataTransfer?.types;
        movingRef.current = !!types && Array.from(types).includes('application/x-lexical-drag');
        // 이번 태스크가 끝나면 반드시 내린다 — 드롭했는데 재삽입이 오지 않는 경우(다른 노드를
        // 옮겼다 등)에 이 값이 남아 다음 붙여넣기를 조용히 통과시키면 안 된다.
        if (movingRef.current) setTimeout(() => { movingRef.current = false; }, 0);
        return false; // 관찰만 한다 — 실제 드롭 처리는 ImagesPlugin 이 그대로 한다
      },
      // 가장 높은 우선순위로 **먼저 보고 넘긴다.** ImagesPlugin 이 DROP 을 HIGH 로 잡아 true 를
      // 돌려주므로, 그보다 낮으면 이 관찰이 아예 돌지 않는다.
      COMMAND_PRIORITY_CRITICAL,
    );
    const unregisterInsert = editor.registerCommand<InsertImagePayload>(
      INSERT_IMAGE_COMMAND,
      (payload) => {
        // 자리만 옮기는 중이면 묻지 않고 통과시킨다 — 묻는 순간 그림이 사라진다(movingRef 주석).
        if (movingRef.current) {
          movingRef.current = false;
          return false;
        }
        // 명령 리스너는 update 안에서 돌므로 `$` 함수를 그대로 쓸 수 있다.
        const precedingSrcs = $collectPrecedingImageSrcs();
        onPasteImage({
          field,
          src: payload?.src,
          altText: payload?.altText ?? '',
          precedingSrcs,
        });
        // **항상 가로챈다.** 「본문에 그냥 넣기」를 고른 경우에도 여기서 통과시키지 않는다 —
        // 통과시키면 호스트가 창을 띄우기도 전에 노드가 들어가 되돌릴 수 없고, 통과 여부를
        // 콜백의 반환값으로 정하면 창이 비동기라 그 값을 제때 알 수 없다.
        // 넣는 일은 호스트가 편집기 손잡이(insertImage)로 직접 한다.
        return true;
      },
      // ImagesPlugin 은 COMMAND_PRIORITY_EDITOR(가장 낮음)로 등록한다 — 그 위에서 먼저 받는다.
      COMMAND_PRIORITY_HIGH,
    );
    return () => { unregisterDrop(); unregisterInsert(); };
  }, [editor, onPasteImage, field]);

  return null;
}
